import React, { memo } from 'react';
import {
    Box,
    Paper,
    Typography,
    useTheme,
    Checkbox,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { alpha } from '@mui/material/styles';
import { Comment as CommentIcon, CheckCircle as CheckCircleIcon } from '@mui/icons-material';

import { DeviceInfo, LabelInfo, CalibrationType } from './types';
import { getOperatorColor, getReagentColor, reagentPriorities, getReagentPosition } from './utils';
import DeviceInfoTooltip from './DeviceInfoTooltip';

// Helper functions
const isRecentTimestamp = (timestamp: string | undefined): boolean => {
    if (!timestamp) return true;
    
    const now = new Date();
    const dataTime = new Date(timestamp);
    
    // Calculate the difference in milliseconds
    const diffTime = now.getTime() - dataTime.getTime();
    
    // Convert to days
    const diffDays = diffTime / (1000 * 60 * 60 * 24);
    
    // Consider timestamps from the last 7 days as recent
    return diffDays <= 7;
};

const formatCompactDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    
    const isToday = date.toDateString() === now.toDateString();
    const isYesterday = date.toDateString() === yesterday.toDateString();
    
    if (isToday) {
        return `Today ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    } else if (isYesterday) {
        return `Yesterday ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    } else {
        return date.toLocaleDateString([], { day: '2-digit', month: '2-digit' }) + 
               ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
};

interface CalibrationIndicatorsProps {
    calibrations?: CalibrationType[];
    position?: { 
        top?: string | number; 
        right?: string | number; 
        bottom?: string | number; 
        left?: string | number;
        transform?: string;
    };
    theme?: any;
}

const CalibrationIndicators: React.FC<CalibrationIndicatorsProps> = ({ 
    calibrations, 
    position = { top: '24px', right: '24px' }, 
    theme 
}) => {
    if (!calibrations || calibrations.length === 0) return null;
    
    // Prepare a 9-element array (3x3) filled with nulls
    const grid = Array(9).fill(null);
    
    // Fill the grid with reagents if they exist
    if (calibrations && calibrations.length > 0) {
        // Sort calibrations by reagent priorities
        const sortedCalibrations = [...calibrations].sort((a, b) => {
            const priorityA = reagentPriorities[a.reagent] ?? 999;
            const priorityB = reagentPriorities[b.reagent] ?? 999;
            return priorityA - priorityB;
        });
        
        // Set of used positions
        const usedPositions = new Set<number>();
        
        // Fill the grid with reagents, starting with the highest priority
        sortedCalibrations.forEach(cal => {
            const reagent = cal.reagent;
            
            // Use function for dynamic position assignment
            const position = getReagentPosition(reagent, usedPositions);
            
            // If the position is already taken (e.g., by PM), ignore this reagent
            if (!grid[position]) {
                grid[position] = reagent;
                usedPositions.add(position);
            }
        });
    }
    
    const isDarkMode = theme?.name === 'dark';
    
    // Prepare a string with the list of reagents to display in the tooltip
    const reagentsString = calibrations && calibrations.length > 0
        ? calibrations
            .map(cal => cal.reagent)
            .filter((value, index, self) => self.indexOf(value) === index) // unique values
            .join(', ')
        : 'No calibrations';
    
    return (
        <Box 
            sx={{ 
                position: 'absolute',
                zIndex: 10,
                ...position,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
            }}
            title={`Calibrations: ${reagentsString}`}
        >
            
            <Box
                sx={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(3, 9px)', 
                    gridTemplateRows: 'repeat(3, 9px)', 
                    gap: '4px',
                    backgroundColor: isDarkMode ? 'rgba(0, 0, 0, 0.4)' : 'rgba(255, 255, 255, 0.6)',
                    padding: '4px',
                    borderRadius: '5px',
                    border: `1px solid ${theme.palette.borders?.primary || theme.palette.divider}`,
                }}
            >
                {grid.map((reagent, index) => (
                    <Box
                        key={index}
                        sx={{
                            width: '9px',
                            height: '9px',
                            borderRadius: '50%',
                            backgroundColor: reagent ? getReagentColor(reagent) : 'transparent',
                            border: reagent 
                                ? `1px solid ${isDarkMode ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.3)'}` 
                                : '1px solid rgba(128,128,128,0.2)',
                            boxShadow: reagent 
                                ? `0px 0px 3px ${getReagentColor(reagent)}`
                                : 'none',
                            position: 'relative'
                        }}
                    />
                ))}
            </Box>
        </Box>
    );
};

// Memoize CalibrationIndicators for better performance
const MemoizedCalibrationIndicators = memo(CalibrationIndicators);

interface DeviceCardProps {
    device: DeviceInfo;
    selectionMode?: boolean;
    isSelected?: boolean;
    onSelect?: () => void;
    onDevicesUpdated?: () => void;
    onClick?: (device: DeviceInfo) => void;
    compact?: boolean;
}

// Custom prop comparator for React.memo to prevent unnecessary re-renders
const propsAreEqual = (prevProps: DeviceCardProps, nextProps: DeviceCardProps): boolean => {
    // Compare only the essential props that affect rendering
    return (
        prevProps.device.box === nextProps.device.box &&
        prevProps.device.operator === nextProps.device.operator &&
        prevProps.device.time_stamp === nextProps.device.time_stamp &&
        prevProps.device.modem === nextProps.device.modem &&
        prevProps.device.iccid === nextProps.device.iccid &&
        prevProps.isSelected === nextProps.isSelected &&
        prevProps.selectionMode === nextProps.selectionMode &&
        // Compare comments existence
        !!prevProps.device.comments === !!nextProps.device.comments &&
        // Compare label length 
        (prevProps.device.label?.length || 0) === (nextProps.device.label?.length || 0) &&
        // Compare calibrations length
        (prevProps.device.calibrations?.length || 0) === (nextProps.device.calibrations?.length || 0)
    );
};

const DeviceCardComponent: React.FC<DeviceCardProps> = ({ 
    device, 
    selectionMode = false, 
    isSelected = false, 
    onSelect = () => {}, 
    onDevicesUpdated = () => {},
    onClick,
    compact = false,
}) => {
    const theme = useTheme();
    const navigate = useNavigate();

    const handleClick = (e: React.MouseEvent) => {
        if (onClick) {
            onClick(device);
        } else if (selectionMode) {
            onSelect();
        } else {
            navigate(`/device/${device.box}`);
        }
    };

    const isRecent = isRecentTimestamp(device.time_stamp);
    const cleanModem = typeof device.modem === 'string' && device.modem.trim() !== '' 
        ? device.modem.replace(/\r|\n/g, '').trim() 
        : null;

    // Calibration position - centered at the top of the card
    const calibrationPosition = { top: '65px', left: '65%', transform: 'translateX(-50%)' };

    return (
        <Paper
            onClick={handleClick}
            sx={{
                p: 1.5,
                width: compact ? '140px' : '180px',
                height: compact ? '100px' : '120px',
                display: 'flex',
                flexDirection: 'column',
                cursor: 'pointer',
                position: 'relative',
            borderLeft: isSelected ? 'none' : `4px solid ${getOperatorColor(device.operator || 'Unknown')}`,
            // Add top/right/bottom borders from theme and keep left as operator color
            borderTop: `1px solid ${theme.palette.borders?.primary || theme.palette.divider}`,
            borderRight: `1px solid ${theme.palette.borders?.primary || theme.palette.divider}`,
            borderBottom: `1px solid ${theme.palette.borders?.primary || theme.palette.divider}`,
            // Card surface should be on paper, container provides default background
            backgroundColor: theme.palette.background.paper,
            ...(isSelected && { backgroundColor: alpha(theme.palette.primary.main, 0.1) }),
            ...(!isSelected && !isRecent && device.time_stamp && { backgroundColor: alpha(theme.palette.warning.light, 0.15) }),
                boxShadow: isSelected 
                    ? `0 0 0 2px ${theme.palette.primary.main}, ${theme.shadows[1]}` 
                    : theme.shadows[1],
                transition: 'all 0.2s',
                '&:hover': {
                backgroundColor: isSelected 
                    ? alpha(theme.palette.primary.main, 0.15)
                    : !isRecent && device.time_stamp
                        ? alpha(theme.palette.warning.light, 0.25)
                        : alpha(theme.palette.action.hover, 0.1),
                    transform: !selectionMode ? 'translateY(-2px)' : 'none',
                    boxShadow: isSelected
                        ? `0 0 0 2px ${theme.palette.primary.main}, ${theme.shadows[2]}` 
                        : !selectionMode ? theme.shadows[4] : theme.shadows[1]
                },
                overflow: 'visible'
            }}
            aria-selected={isSelected}
            role={selectionMode ? "option" : undefined}
        >
            {/* Checkbox dla trybu wyboru */}
            {selectionMode && (
                <Box 
                    sx={{ 
                        position: 'absolute', 
                        top: '6px', 
                        right: '6px', 
                        zIndex: 20,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <Checkbox
                        checked={isSelected}
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation();
                            onSelect();
                        }}
                        sx={{
                            padding: 0,
                            color: isSelected 
                                ? theme.palette.primary.main 
                                : alpha(theme.palette.text.secondary, 0.7),
                            '&.Mui-checked': {
                                color: theme.palette.primary.main,
                            },
                        }}
                    />
                </Box>
            )}

            {/* Calibration indicators - placed in absolute position */}
            <MemoizedCalibrationIndicators 
                calibrations={device.calibrations} 
                position={calibrationPosition}
                theme={theme}
            />

            {/* Box number with labels */}
            <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                mb: 0.5,
                overflow: 'hidden',
                pr: selectionMode ? 2 : 0,
            }}>
                <Typography variant="body2" fontWeight="bold" sx={{ 
                    flexShrink: 0,
                    ...(selectionMode && isSelected && {
                        color: theme.palette.primary.main,
                    })
                }}>
                    {device.box}
                </Typography>
                {device.label && device.label.length > 0 && (
                    <Box sx={{ 
                        display: 'flex', 
                        gap: 0.5, 
                        ml: 1, 
                        overflow: 'hidden',
                        flexWrap: 'nowrap',
                        maxWidth: 'calc(100% - 50px)',
                    }}>
                        {device.label.slice(0, 3).map((labelObj, index) => (
                            <Typography
                                key={index}
                                variant="caption"
                                sx={{
                                    px: 0.5,
                                    py: 0,
                                    borderRadius: '3px',
                                    fontSize: '0.6rem',
                                    lineHeight: '1.2',
                                    backgroundColor: labelObj.color 
                                        ? alpha(labelObj.color, theme.name === 'dark' ? 0.2 : 0.1)
                                        : (theme.name === 'dark' 
                                            ? alpha(theme.palette.primary.main, 0.2)
                                            : alpha(theme.palette.primary.main, 0.1)),
                                    color: labelObj.color
                                        ? labelObj.color
                                        : (theme.name === 'dark'
                                            ? theme.palette.primary.light
                                            : theme.palette.primary.main),
                                    border: `1px solid ${
                                        labelObj.color 
                                            ? alpha(labelObj.color, 0.3)
                                            : alpha(theme.palette.primary.main, 0.2)
                                    }`,
                                    whiteSpace: 'nowrap',
                                    maxWidth: '60px',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis'
                                }}
                                title={labelObj.label}
                            >
                                {labelObj.label}
                            </Typography>
                        ))}
                        {device.label.length > 3 && (
                            <Typography
                                variant="caption"
                                sx={{
                                    px: 0.5,
                                    py: 0,
                                    borderRadius: '3px',
                                    fontSize: '0.6rem',
                                    lineHeight: '1.2',
                                    backgroundColor: theme.name === 'dark' 
                                        ? alpha(theme.palette.grey[600], 0.3)
                                        : alpha(theme.palette.grey[400], 0.2),
                                    color: theme.name === 'dark'
                                        ? theme.palette.grey[300]
                                        : theme.palette.grey[700],
                                    border: `1px solid ${alpha(theme.palette.grey[500], 0.3)}`,
                                    whiteSpace: 'nowrap'
                                }}
                                title={device.label.slice(3).map(l => l.label).join(', ')}
                            >
                                +{device.label.length - 3}
                            </Typography>
                        )}
                    </Box>
                )}
            </Box>

            {/* Rest of card content */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 'auto' }}>
                {device.iccid && device.iccid.trim() !== '' && (
                    <Typography 
                        variant="caption" 
                        sx={{ 
                            display: 'block',
                            fontSize: '0.7rem',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            color: 'text.primary'
                        }}
                        title={device.iccid}
                    >
                        {device.iccid}
                    </Typography>
                )}
                
                {cleanModem && (
                    <Typography 
                        variant="caption" 
                        sx={{ 
                            display: 'block',
                            fontSize: '0.7rem',
                            fontWeight: 'medium',
                            color: theme.name === 'dark' ? '#64b5f6' : '#0d47a1',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                        }}
                        title={cleanModem}
                    >
                        {cleanModem}
                    </Typography>
                )}
            </Box>
            
            {/* Timestamp section */}
            {device.time_stamp && (
                <Box 
                    sx={{ 
                        display: 'flex',
                        alignItems: 'center',
                        mt: 'auto',
                        pt: 0.5,
                        borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`
                    }}
                >
                    <Typography 
                        variant="caption" 
                        sx={{
                            fontSize: '0.65rem',
                            color: !isRecent ? theme.palette.warning.dark : theme.palette.text.secondary,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            width: '100%'
                        }}
                        title={new Date(device.time_stamp).toLocaleString()}
                    >
                        {!isRecent && '⚠ '}
                        {formatCompactDate(device.time_stamp)}
                    </Typography>
                </Box>
            )}

            <Box sx={{ position: 'absolute', right: 4, bottom: 4, display: 'flex', flexDirection: 'column',  }}>
                {device.comments && (
                    <Box
                        sx={{
                            color: theme.palette.info.main,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 20,
                            height: 20,
                            borderRadius: '50%',
                            transition: 'all 0.2s',
                            marginRight: '7px',
                            marginBottom: '4px',
                            '&:hover': {
                                backgroundColor: alpha(theme.palette.info.main, 0.1),
                            }
                        }}
                    >
                        <CommentIcon sx={{ fontSize: '0.9rem' }} />
                    </Box>
                )}
                <Box
                    sx={{
                        color: theme.palette.text.secondary,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 20,
                        height: 20,
                        borderRadius: '50%',
                        transition: 'all 0.2s',
                        '&:hover': {
                            color: theme.palette.primary.main,
                            backgroundColor: alpha(theme.palette.primary.main, 0.1),
                        }
                    }}
                >
                    <DeviceInfoTooltip
                        box={device.box}
                        iccid={device.iccid}
                        modem={device.modem}
                        labels={device.label}
                        timeStamps={device.time_stamps}
                        comments={device.comments}
                        calibrations={device.calibrations}
                    />
                </Box>
            </Box>
        </Paper>
    );
};

// Export the memoized component
const DeviceCard = memo(DeviceCardComponent, propsAreEqual);
export default DeviceCard; 